import React, { createContext, useState, useContext, useEffect } from 'react';

// API Gateway endpoint URL (replace with your actual URL)
const API_ENDPOINT = "https://hg9ykqmu3m.execute-api.us-west-2.amazonaws.com/prod/checkGuest";

const AuthContext = createContext();

const SESSION_EXPIRY = 7 * 24 * 60 * 60 * 1000; // 7 days


export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rememberMe, setRememberMe] = useState(true); // Default to true

// Helper function to check if session is expired
  const isSessionExpired = (timestamp) => {
    if (!timestamp) return true;
    
    const sessionTime = new Date(timestamp).getTime();
    const currentTime = new Date().getTime();
    
    return currentTime - sessionTime > SESSION_EXPIRY;
  };
  
  useEffect(() => {
    // Check if user is already logged in (session storage)
    const checkExistingSession = async () => {
      // First try localStorage (more persistent)
      const localSession = localStorage.getItem('weddingGuestSession');
      // Fallback to sessionStorage
      const session = localSession || sessionStorage.getItem('weddingGuestSession');
      
      if (session) {
        try {
          const sessionData = JSON.parse(session);
          
          // Check if session is expired
          if (isSessionExpired(sessionData.timestamp)) {
            console.log("Session expired, clearing storage");
            localStorage.removeItem('weddingGuestSession');
            sessionStorage.removeItem('weddingGuestSession');
            setLoading(false);
            return;
          }
          
          // Verify email via API Gateway
          const response = await fetch(API_ENDPOINT, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email: sessionData.email })
          });
          
          const data = await response.json();
          
          if (data.success) {
            setCurrentUser(data.guest);
            // Refresh the timestamp to extend the session
            if (localSession) {
              localStorage.setItem('weddingGuestSession', JSON.stringify({
                ...sessionData,
                timestamp: new Date().toISOString()
              }));
            } else {
              sessionStorage.setItem('weddingGuestSession', JSON.stringify({
                ...sessionData,
                timestamp: new Date().toISOString()
              }));
            }
          } else {
            // Email no longer valid, clear session
            localStorage.removeItem('weddingGuestSession');
            sessionStorage.removeItem('weddingGuestSession');
          }
        } catch (err) {
          console.error("Session restoration error:", err);
          localStorage.removeItem('weddingGuestSession');
          sessionStorage.removeItem('weddingGuestSession');
        }
      }
      
      setLoading(false);
    };
    
    checkExistingSession();
  }, []);
  
  // Verify email via API Gateway
  const login = async (email, remember = true) => {
    setLoading(true);
    setError("");
    setRememberMe(remember);
    
    try {
      // Normalize email to lowercase
      const normalizedEmail = email.toLowerCase().trim();
      
      // Call API Gateway endpoint
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: normalizedEmail })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Guest exists, set user and create session
        setCurrentUser(data.guest);
        
        const sessionData = JSON.stringify({
          email: normalizedEmail,
          name: data.guest.name || 'Guest',
          timestamp: new Date().toISOString()
        });
        
        // Store in localStorage if remember me is checked (more persistent)
        if (remember) {
          localStorage.setItem('weddingGuestSession', sessionData);
        }
        
        // Always store in sessionStorage as a fallback
        sessionStorage.setItem('weddingGuestSession', sessionData);
        
        return true;
      } else {
        // Guest not found
        setError(data.message || "Sorry, this email is not on the guest list.");
        return false;
      }
    } catch (err) {
      console.error("Login error:", err);
      setError("An error occurred connecting to the server. Please try again.");
      return false;
    } finally {
      setLoading(false);
    }
  };
  
  const logout = () => {
    setCurrentUser(null);
    localStorage.removeItem('weddingGuestSession');
    sessionStorage.removeItem('weddingGuestSession');
  };
  
  const value = {
    currentUser,
    login,
    logout,
    loading,
    error,
    rememberMe,
    setRememberMe
  };
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};